/**
 * The vein guarantee (D-086, user 2026-09-27): every galaxy has at least `veinGuarantee.min` tetherstone-vein
 * systems, outside the Gate Cluster. Beyond that, veins stay as rare as the archetype weights make them.
 */
import { createRng, nextFloat } from '../core/rng'
import type { GalaxyConfig } from '../data/galaxyConfig'
import { deriveSeed } from './seed'
import type { Archetype, StarSystem } from './types'

export const VEIN: Archetype = 'tetherstone-vein'

/**
 * Picks the systems to convert into veins, in pick order (empty when enough veins already rolled, so those
 * galaxies are unchanged). Eligible: non-anchor systems, not already veins, in a region whose vein weight is above
 * 0 (never the Gate Cluster). The pool is `preferRegions` while it has systems left, then every eligible region.
 * Pools keep index order and picks use `deriveSeed(galaxySeed, 'vein-guarantee')`, so a seed always picks the same.
 */
export function veinConversions(galaxySeed: number, systems: readonly StarSystem[], cfg: GalaxyConfig): number[] {
  const g = cfg.veinGuarantee
  const missing = g.min - systems.filter((s) => s.archetype === VEIN).length
  if (missing <= 0) return []
  const eligible = systems.filter((s) => s.anchor === null && s.archetype !== VEIN && cfg.archetypeWeights[s.region][VEIN] > 0)
  const rng = createRng(deriveSeed(galaxySeed, 'vein-guarantee'))
  const picked: number[] = []
  for (let k = 0; k < missing; k++) {
    const left = eligible.filter((s) => !picked.includes(s.index))
    const preferred = left.filter((s) => g.preferRegions.includes(s.region))
    const pool = preferred.length > 0 ? preferred : left
    if (pool.length === 0) break
    picked.push(pool[Math.floor(nextFloat(rng) * pool.length)].index)
  }
  return picked
}
